import { View, Text, StyleSheet } from '@react-pdf/renderer';
import { getProficiency } from "../../../../../constants/resume";

export default ({ backgroundColor, textColor, languagesAsText, skillsAsText, shouldShowHeader, shouldShowExperience, shouldShowEducation, shouldShowSkills, aboveTitleItems, data, getFontFamily, selectedFont, parseHtmlToText, layout = 'centered' }) => {
    const styles = StyleSheet.create({
        page: {
            width: '100%',
            padding: 20,
            fontFamily: getFontFamily(selectedFont),
        },
        resumeHeader: {
            textAlign: 'left',
            marginBottom: 3,
        },
        aboveTitleWrapper: {
            marginBottom: 5,
            flexDirection: 'row',
            justifyContent: 'flex-start',
            flexWrap: 'wrap',
        },
        aboveTitleItem: {
            fontSize: 8,
            color: '#444',
            marginRight: 4,
        },
        headerName: {
            fontSize: 18,
            fontWeight: 700,
            fontWeight: 'bold',
            margin: 0,
            letterSpacing: 1,
            fontFamily: getFontFamily(selectedFont),
        },
        headerTitle: {
            fontSize: 11,
            fontStyle: 'italic',
            fontWeight: 400,
            margin: '4px 0 8px 0',
            color: '#444',
        },
        resumeSummary: {
            marginBottom: 15,
        },
        summaryText: {
            margin: 0,
            fontSize: 10,
            textAlign: 'left',
        },
        resumeSection: {
            marginBottom: 5,
            width: '100%',
        },
        sectionTitle: {
            fontSize: 10,
            fontWeight: 'bold',
            textAlign: 'left',
            marginTop: 10,
            marginBottom: 2,
            letterSpacing: 1,
            borderBottomWidth: 1,
            borderBottomColor: '#c7c5c5',
            borderBottomStyle: 'solid',
            paddingBottom: 2,
        },
        bigSections: {},
        smallSections: {},
        mainSections: {},
        bigSectionItem: {
            width: '100%',
            marginBottom: 10,
        },
        bigSectionItemHeader: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%'
        },
        bigSectionHeaderLeft: {
            width: '70%',
        },
        bigSectionHeaderRight: {
            width: '30%',
        },
        bigSectionDescription: {
            marginBottom: 20,
            marginTop: 10
        },
        preTitle: {
            textTransform: 'uppercase',
            fontSize: 10,
            fontWeight: 600,
        },
        mainTitle: {
            fontSize: 10,
            fontWeight: 600,
            fontStyle: 'italic',
        },
        description: {
            fontSize: 10,
            textAlign: 'left',
            color: '#000',
            marginTop: 10,
            marginBottom: 0,
        },
        dates: {
            fontSize: 9,
            fontWeight: 600,
            textAlign: 'right',
            whiteSpace: 'nowrap'
        },
        location: {
            fontSize: 10,
            fontWeight: 600,
            fontStyle: 'italic',
        },
        skillsSection: {
            marginBottom: 15,
        },
        skillItems: {
            fontSize: 10,
            padding: 0,
            margin: 0,
        },
    });

    // Dynamic layout logic
    if (layout !== 'centered') {
        styles.bigSections = {
            ...styles.bigSections,
            width: '70%',
        }
        styles.smallSections = {
            ...styles.smallSections,
            width: '30%',
        }
        styles.mainSections = {
            ...styles.mainSections,
            flexDirection: layout === 'left' ? 'row-reverse' : 'row',
            justifyContent: 'space-between',
            width: '100%',
            gap: 20,
        }
    }

    return (
        <>
            {shouldShowHeader && (
                <View style={styles.resumeHeader}>
                    <Text style={styles.headerName}>{`${data.firstName || ''} ${data.lastName || ''}`.trim()}</Text>
                    {data.title && <Text style={styles.headerTitle}>{data.title}</Text>}
                    <View style={styles.aboveTitleWrapper}>
                        {aboveTitleItems.map((item, idx) => (
                            <Text key={idx} style={styles.aboveTitleItem}>
                                {item}{idx < aboveTitleItems.length - 1 ? ' •' : ''}
                            </Text>
                        ))}
                    </View>
                </View>
            )}

            {data.summary && (
                <View style={styles.resumeSummary}>
                    <Text style={styles.sectionTitle}>Professional Summary</Text>
                    {parseHtmlToText(data.summary, styles.summaryText)}
                </View>
            )}

            <View style={styles.mainSections}>
                <View style={styles.bigSections}>
                    {shouldShowExperience && (
                        <View style={styles.resumeSection}>
                            <Text style={styles.sectionTitle}>Employment History</Text>
                            {data.experience.map((job, idx) => (
                                <View style={styles.bigSectionItem} key={idx}>
                                    <View style={styles.bigSectionItemHeader}>
                                        <View style={styles.bigSectionHeaderLeft}>
                                            <Text style={styles.preTitle}>{job.jobPosition}</Text>
                                            <Text style={styles.mainTitle}>{job.companyName}</Text>
                                        </View>
                                        <View style={styles.bigSectionHeaderRight}>
                                            {(job.startDate?.month || job.startDate?.year) && (
                                                <Text style={styles.dates}>
                                                    {job.startDate?.month ? `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][job.startDate.month - 1]} ` : ''}
                                                    {job.startDate?.year} - {job.isPresent ? 'Present' : `${job.endDate?.month ? ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][job.endDate.month - 1] + ' ' : ''}${job.endDate?.year || ''}`}
                                                </Text>
                                            )}
                                            {job.location && <Text style={styles.location}>{job.location}</Text>}
                                        </View>
                                    </View>
                                    <View style={styles.bigSectionDescription}>
                                        <Text>{job.description && parseHtmlToText(job.description, styles.description)}</Text>
                                    </View>
                                </View>
                            ))}
                        </View>
                    )}
                    {shouldShowEducation && (
                        <View style={styles.resumeSection}>
                            <Text style={styles.sectionTitle}>Education</Text>
                            {data.education.map((ed, idx) => (
                                <View style={styles.bigSectionItem} key={idx}>
                                    <View style={styles.bigSectionItemHeader}>
                                        <View style={styles.bigSectionHeaderLeft}>
                                            <Text style={styles.preTitle}>{ed.degreeName} {ed.courseName ? `(${ed.courseName})` : ''}</Text>
                                            <Text style={styles.mainTitle}>{ed.universityName}</Text>
                                            {ed.description && parseHtmlToText(ed.description, styles.description)}
                                        </View>
                                        <View style={styles.bigSectionHeaderRight}>
                                            <Text style={styles.dates}>
                                                {ed.startDate?.month ? `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][ed.startDate.month - 1]} ` : ''}{ed.startDate?.year} - {ed.endDate?.month ? `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][ed.endDate.month - 1]} ` : ''}{ed.endDate?.year}
                                            </Text>
                                            {ed.location && <Text style={styles.location}>{ed.location}</Text>}
                                        </View>
                                    </View>
                                </View>
                            ))}
                        </View>
                    )}
                </View>

                <View style={styles.smallSections}>
                    {shouldShowSkills && (
                        <View style={styles.resumeSection}>
                            <Text style={styles.sectionTitle}>Skills</Text>
                            <Text style={styles.skillItems}>
                            {(data.skills || []).map(skill => skill.name).filter(a => !!a).join(', ')}
                            </Text>
                        </View>
                    )}

                    {languagesAsText?.length && (
                        <View style={styles.resumeSection}>
                            <Text style={styles.sectionTitle}>Languages</Text>
                            <Text style={styles.skillItems}>
                                {languagesAsText}
                            </Text>
                        </View>
                    )}
                </View>
            </View>
        </>
    )
}